export interface Project {
  id: string;
  icon: string;
  iconBackground: {
    from: string;
    to: string;
  };
  textColor: string;
  gradient: {
    from: string;
    to: string;
  };
  pattern: 'circles' | 'grid' | 'dots' | 'waves' | 'hexagons' | 'stars';
  title: string;
  description: string;
  shortDescription: string;
  type: string;
  technologies: string[];
  techParagraph: string;
  category:
    | 'AI'
    | 'Organize'
    | 'Motion'
    | 'Digital'
    | 'Analytics'
    | 'Kubernetes'
    | 'ML'
    | 'Transportation'
    | 'Infrastructure';

  githubUrl?: string;
  liveUrl?: string;
  image?: string;
  tags: string[];
  stats: {
    label: string;
    value: string;
  }[];
  projectObjective: string[];
  solutionApproach: string;
  challenges: string[];
  keyAchievements: string[];
  lessonsLearned: string[];
  businessImpact: string[];
}

export const projectsData: Project[] = [
  {
    id: 'project-1',
    textColor: 'text-blue-300',
    title: 'RAG System',
    description:
      'An AI-powered platform designed to enhance clinical decision-making through evidence-based recommendations, real-time safety alerts, and seamless EHR integration.',
    shortDescription:
      'An AI-Powered platform for enhancing clinical decision-making.',
    gradient: {
      from: 'from-blue-500/20',
      to: 'to-cyan-600/20',
    },
    pattern: 'hexagons',
    icon: 'Stethoscope',
    iconBackground: {
      from: 'from-blue-500',
      to: 'to-cyan-600',
    },
    type: 'Health',
    techParagraph:
      'Engineered with Python for ML backend, TensorFlow for deep learning models, FastAPI for high-performance APIs, React for dynamic frontend, PostgreSQL for data persistence, and Redis for real-time caching and session management.',
    technologies: [
      'Python',
      'TensorFlow',
      'FastAPI',
      'React',
      'PostgreSQL',
      'Redis',
    ],
    category: 'AI',

    githubUrl: 'https://github.com/username/portfolio',
    liveUrl: 'https://cds.ejmalek.com',
    tags: ['ai', 'machine-learning', 'recommendations', 'python'],
    stats: [
      { label: 'Duration', value: '9 months' },
      { label: 'Team Size', value: '4' },
      {
        label: 'Technologies',
        value: 'Python, TensorFlow, FastAPI, React, PostgreSQL, Redis',
      },
    ],
    projectObjective: [
      'Develop an advanced AI-powered recommendation system that addresses Healthcare professionals facing critical challenges in clinical decision-making such as:',
      'Information Overload - Medical knowledge doubles every 73 days, making it impossible to stay current',
      'Medical Errors - 250,000+ deaths annually in the US due to medical errors, many preventable',
      'Workflow Disruption - Existing decision support tools interrupt clinical workflows',
      'Inconsistent Care - Variation in care quality based on provider knowledge and experience',
      'Time Constraints - Average of 16+ minutes per patient visit spent searching for clinical information',
    ],
    solutionApproach:
      'The solution was built using a combination of machine learning algorithms, including collaborative filtering, content-based filtering, and hybrid recommendation systems. The system was designed to be scalable and efficient, and it was built using modern web technologies.',
    challenges: ['Scalability', 'Performance', 'Data Privacy', 'User Privacy'],
    keyAchievements: [
      'Clinical accuracy: ≥ 90% (vs baseline manual processes)',
      'Safety score: ≥ 95% (drug interactions, contraindications)',
      'User satisfaction: ≥ 4.0/5.0 (clinical staff feedback)',
      'Time to clinical decision: ≤ 2 minutes (vs 16+ minutes baseline)',
      'Reduction in medical errors: ≥ 40%',
      'ROI achievement: 200%+ within 18 months',
    ],
    lessonsLearned: [
      'The importance of data privacy and security',
      'The importance of performance optimization',
      'The importance of scalability',
      'The importance of user experience',
    ],
    businessImpact: [
      'The investment in this comprehensive Clinical Decision Support System will generate substantial returns through improved patient outcomes, operational efficiency, and competitive advantage in the healthcare marketplace.',
      '• ROI achievement: Target 200 % + by month 18',
      '• Cost per clinical decision: Target ≤ $0.50',
      '• Revenue impact: Target $5M + annually',
      '• Market differentiation score: Target top 10 % ',
    ],
  },
  {
    id: 'project-2',
    textColor: 'text-teal-300',
    title: 'ML Training Pipeline',
    description:
      "a scalable, distributed machine learning training platform that addresses critical gaps in enterprise ML infrastructure. The platform enables organizations to train, optimize, and deploy ML models at scale using Kubernetes orchestration and Ray's distributed computing framework.",
    shortDescription:
      'A scalable, distributed machine learning training platform.',
    gradient: {
      from: 'from-teal-500/20',
      to: 'to-green-500/20',
    },
    pattern: 'grid',
    icon: 'Blocks',
    iconBackground: {
      from: 'from-teal-500',
      to: 'to-green-500',
    },
    type: 'ML Training',
    techParagraph:
      'PyTorch for training, Ray Tune for Hyperparameter Optimization, Rest API for deployment, and Kubernetes for infrastructure management.',
    technologies: ['PyTorch', 'FastAPI', 'React', 'Docker', 'AI'],
    category: 'Analytics',

    githubUrl: 'https://github.com/username/',
    liveUrl: 'https://ray-cluster.ejmalek.com',
    tags: ['model training', 'fullstack', 'mongodb', 'computer vision'],
    stats: [
      { label: 'Duration', value: '6 months' },
      { label: 'Team Size', value: '3' },
      { label: 'Technologies', value: 'PyTorch, FastAPI, React, Docker, AI' },
    ],
    projectObjective: [
      'To enable any organization to build, train, and deploy ML models at scale with enterprise-grade reliability and efficiency.',
    ],
    solutionApproach:
      'The solution was built using PyTorch for training, Ray Tune for Hyperparameter Optimization, Rest API for deployment, and Kubernetes for infrastructure management.',
    challenges: [
      'Scalability',
      'Performance',
      'Limitations',
      'Manual Hyperparameter Tuning',
    ],
    keyAchievements: [
      'Reduce model training time by 5x through distributed computing',
      'Automate hyperparameter optimization to improve model accuracy by 15-20%',
      'Decrease time-to-production from weeks to hours',
      'Achieve 80%+ resource utilization across compute clusters',
      'Provide complete experiment reproducibility and tracking',
    ],
    lessonsLearned: [
      'The importance of scalability',
      'The importance of performance optimization',
    ],
    businessImpact: [
      '1. Time to production: < 24 hours',
      '2. Model iteration speed: 10x improvement',
      '3. Infrastructure cost: 40% reduction',
      '4. User adoption: 80% of ML team within 3 months',
    ],
  },
  {
    id: 'project-3',
    textColor: 'text-purple-300',
    title: 'B2B Multi-Modal Logistics',
    description:
      'An event-driven microservices platform for B2B logistics operations, featuring CQRS, Event Sourcing, and distributed event streaming with Apache Kafka',
    shortDescription:
      'An Event-Driven Microservices platform for B2B logistics operations.',
    gradient: {
      from: 'from-purple-500/20',
      to: 'to-indigo-600/20',
    },
    pattern: 'dots',
    icon: 'Barcode',
    iconBackground: {
      from: 'from-purple-500',
      to: 'to-indigo-600',
    },
    type: 'Logistics',
    techParagraph:
      'Powered by NodeJS for backend capabilities, PostrgeSQL for database, Redis for caching, MongoDB for document-based storage, and Docker for containerization.',
    technologies: ['NodeJS', 'MongoDB', 'Docker', 'PostgreSQL', 'Redis'],
    category: 'Analytics',

    githubUrl: 'https://logistics.ejmalek.com/login',
    liveUrl: 'https://logistics.ejmalek.com',
    tags: ['task-management', 'collaboration', 'firebase', 'react'],
    stats: [
      { label: 'Duration', value: '3 months' },
      { label: 'Team Size', value: '2' },
      {
        label: 'Technologies',
        value: 'NodeJS, PostrgeSQL, Redis, MongoDB, Docker',
      },
    ],
    projectObjective: [
      'Develop an event-driven microservices platform for B2B logistics operations, featuring CQRS, Event Sourcing, and distributed event streaming with Apache Kafka',
    ],
    solutionApproach:
      'The solution was built with microservices architecture using NodeJS, PostrgeSQL, Redis, MongoDB, and Docker. The system was designed to be scalable and efficient, and it was built using modern web technologies.',
    challenges: [
      'Real-time updates',
      'Event streaming',
      'Event sourcing',
      'Performance',
    ],
    keyAchievements: [
      'Implemented CQRS and Event Sourcing for event-driven architecture',
      'Implemented distributed event streaming with Apache Kafka',
      'Built real-time event updates and notifications system',
      'Created responsive dashboard for B2B logistics operations',
    ],
    lessonsLearned: [
      'The importance of real-time updates',
      'The importance of event streaming',
      'The importance of event sourcing',
      'The importance of performance optimization',
    ],
    businessImpact: [
      'Enables B2B logistics operations to manage their operations efficiently while providing their clients with a smooth booking experience, reducing administrative overhead by 60%.',
    ],
  },
  {
    id: 'project-4',
    textColor: 'text-yellow-300',
    title: 'SmartGrid',
    description:
      'SmartGrid AI Monitor is a next-generation infrastructure monitoring platform that leverages GPU-accelerated computervision and 3D visualization to transform how energy utilities manage, inspect, and maintain critical assets. The platformprovides real-time, autonomous monitoring with predictive maintenance capabilities, delivering unprecedented operationalefficiency and safety improvements.',
    shortDescription: 'GPU-Accelerated computer vision for energy & utilities.',
    gradient: {
      from: 'from-yellow-500/20',
      to: 'to-orange-600/20',
    },
    pattern: 'waves',
    icon: 'HousePlug',
    iconBackground: {
      from: 'from-yellow-500',
      to: 'to-orange-600',
    },
    type: 'Energy',
    techParagraph:
      'RESTful API for data access, Webhook support for events, SCADA system integration, GIS system integration, Maintenance management system integration',
    technologies: [
      'NodeJS',
      'ThreeJS',
      'TypeScript',
      'PostgreSQL',
      'FastAPI',
      'Docker',
    ],
    category: 'Infrastructure',

    liveUrl: 'https://smartgrid.ejmalek.com',
    tags: ['energy', 'infrastructure', 'computer-vision', 'machine-learning'],
    stats: [
      { label: 'Duration', value: '2 months' },
      { label: 'Team Size', value: '1' },
      {
        label: 'Technologies',
        value: 'NodeJS, ThreeJS, TypeScript, PostgreSQL, FastAPI, Docker',
      },
    ],
    projectObjective: [
      'A comprehensive AI platform that:',
      'Detects defects with 99.2% accuracy in real-time',
      'Processes video at 120+ FPS with <50ms latency',
      'Visualizes assets in interactive 3D with thermal overlays',
      'Predicts failures 48+ hours in advance',
      'Reduces costs by 84% compared to manual inspection',
    ],
    solutionApproach: '',
    challenges: [
      'Real-time video processing',
      '3D visualization',
      'Predictive maintenance',
      'Cost reduction',
    ],
    keyAchievements: [
      'Revenue Target: $50M ARR by Year 3',
      'Market Penetration: 8% of North American utility market',
      'Customer Acquisition: 100+ enterprise clients by Year 2',
      'ROI for Customers: < 12 months payback period',
    ],
    lessonsLearned: [''],
    businessImpact: [
      '87% reduction in inspection costs ($2.08M annual savings)',
      '99.3% defect detection rate',
      'Prevented 3 major outages in first year (estimated $15M in avoided costs)',
      'ROI achieved in 4.2 months',
    ],
  },
  {
    id: 'project-5',
    textColor: 'text-emerald-300',
    title: 'Autonomous Vehicle Training',
    description:
      'A state-of-the-art computer vision system designed to enable safe and reliable autonomous vehicle navigation. Overcoming critical challenges in real-time perception by creating a unified multi-task learning system that simultaneously performs object detection, semantic segmentation, depth estimation, and lane detection while achieving industry-leading performance metrics.',
    shortDescription:
      'A computer vision system designed to enable autonomous vehicle navigation.',
    gradient: {
      from: 'from-cyan-300/20',
      to: 'to-emerald-500/20',
    },
    pattern: 'circles',
    icon: 'CarFront',
    iconBackground: {
      from: 'from-cyan-300',
      to: 'to-emerald-500',
    },
    type: 'ML',
    techParagraph:
      'The ML Framework uses PyTorch 2.0+  as the ideal ecosystem for research-to-production pipeline. Ray 2.8+  is used for distributed training for scaling with native PyTorch integration. Model serving utilizes Ray Serve/NVIDIA Triton for low latency and hihg throughput. Orchestration uses the proven scalability of Kubernetes. Prometheus + Grafana monitors for real-time metrics with sub-second alerting.',
    technologies: [
      'PyTorch',
      'Ray',
      'Ray Serve',
      'NVIDIA Triton',
      'Kubernetes',
      'Prometheus',
      'Grafana',
    ],
    category: 'Transportation',

    githubUrl: 'https://github.com/username/design-system',
    liveUrl: 'https://avcs.ejmalek.com',
    tags: ['autonomous-vehicle', 'computer-vision', 'machine-learning'],
    stats: [
      { label: 'Duration', value: '1 month' },
      { label: 'Team Size', value: '1' },
      {
        label: 'Technologies',
        value:
          'PyTorch, Ray, Ray Serve, NVIDIA Triton, Kubernetes, Prometheus, Grafana',
      },
    ],
    projectObjective: [
      'Create a revolutionary single-backbone neural network with task-specific heads that could perform all perception tasks simultaneously. Eliminate redundant computation while maintaining high accuracy across all tasks.',
    ],
    solutionApproach:
      'The solution was built using a combination of machine learning technologies, including PyTorch, Ray, Ray Serve, NVIDIA Triton, Kubernetes, Prometheus, and Grafana. The system was designed to be scalable and efficient, and it was built using modern web technologies.',
    challenges: [
      'Real-time perception',
      'Multi-task learning',
      'Performance',
      'Scalability',
    ],
    keyAchievements: [
      'Developed a unified multi-task learning system that simultaneously performs object detection, semantic segmentation, depth estimation, and lane detection while achieving industry-leading performance metrics.',
      'Developed a state-of-the-art computer vision system designed to enable safe and reliable autonomous vehicle navigation.',
      'Developed a computer vision system designed to enable safe and reliable autonomous vehicle navigation.',
      'Developed a computer vision system designed to enable safe and reliable autonomous vehicle navigation.',
    ],
    lessonsLearned: [
      'The importance of real-time perception',
      'The importance of multi-task learning',
      'The importance of performance',
      'The importance of scalability',
    ],
    businessImpact: [
      'Cost Efficiency:',

      'Deployment cost reduced to $95/vehicle (target: <$100)',
      '• 75% reduction in computational resources compared to multi-model approach',
      '• Energy consumption at 45W (target: <50W), extending vehicle range by 5%',
      ' Development Velocity:',
      '• Achieved 2-week iteration cycles',
      '• 6x faster training enables rapid experimentation',
      '• Reduced time-to-market by 3 months',
      '• Reduced time-to-market by 3 months',

      'Safety and Reliability:',
      '• Zero safety incidents in 10,000+ hours of testing',
      '• 99.9% uptime (8.76 hours maximum downtime/year)',
      '• Automatic failover in <1 second',
    ],
  },
  {
    id: 'project-6',
    textColor: 'text-rose-300',
    title: 'AI Finance Intelligence System',
    description:
      'An automated financial technology platform that leverages real-time market data, artificial intelligence, and algorithmic optimization to provide institutional-grade portfolio management capabilities. The system continuously monitors market conditions, optimizes asset allocation, and executes rebalancing strategies while maintaining comprehensive risk management protocols.',
    shortDescription:
      'An AI-powered automated portfolio management platform with institutional-grade optimization and risk management.',
    gradient: {
      from: 'from-rose-600/20',
      to: 'to-fuchsia-600/20',
    },
    pattern: 'stars',
    icon: 'ChartLine',
    iconBackground: {
      from: 'from-rose-600',
      to: 'to-fuchsia-600',
    },
    type: 'Finance',
    techParagraph:
      'Built with n8n for workflow orchestration, PostgreSQL for data persistence, Redis for real-time caching, OpenAI GPT-4 for AI analysis, React/Next.js for responsive UI, and Docker/Kubernetes for scalable deployment.',
    technologies: [
      'n8n',
      'PostgreSQL',
      'Redis',
      'OpenAI GPT-4',
      'React',
      'Next.js',
      'Docker',
      'Kubernetes',
    ],
    category: 'AI',

    githubUrl: 'https://github.com/username/portfolio-intelligence',
    liveUrl: 'https://finance.ejmalek.com',
    image: '/images/projects/N8NProject.png',
    tags: [
      'ai',
      'portfolio-management',
      'financial-optimization',
      'risk-management',
      'automation',
    ],
    stats: [
      { label: 'Duration', value: '6 months' },
      { label: 'Team Size', value: '3' },
      {
        label: 'Technologies',
        value:
          'n8n, PostgreSQL, Redis, OpenAI GPT-4, React, Next.js, Docker, Kubernetes',
      },
    ],
    projectObjective: [
      'Democratize access to sophisticated portfolio management tools by creating an intelligent, automated system that delivers professional-grade investment optimization previously available only to institutional investors.',
      'Address critical market challenges:',
      '• Manual portfolio management inefficiencies and emotional bias',
      '• Lack of real-time optimization tools for individual investors',
      '• High costs of institutional-grade analytics for retail investors',
      '• Complex integration of multiple data sources and AI analysis',
      '• Market sentiment analysis requiring vast data processing capabilities',
    ],
    solutionApproach:
      'Implemented a comprehensive AI-powered platform featuring real-time data aggregation from multiple sources (Polygon.io, Alpha Vantage, Yahoo Finance), Modern Portfolio Theory optimization algorithms, AI-powered market analysis using GPT-4, comprehensive risk management with VaR/CVaR calculations, and automated execution engine with smart order routing.',
    challenges: [
      'Real-time data processing with <2 second latency',
      'Portfolio optimization for 1000+ securities simultaneously',
      'AI integration for natural language market analysis',
      'Risk management with multiple metrics and alerting',
      'Automated trade execution with circuit breakers',
      'Scalability to handle 1000+ concurrent analyses',
    ],
    keyAchievements: [
      'Portfolio Sharpe Ratio > 1.5 (target achieved)',
      'System uptime 99.9% (exceeded target)',
      'Optimization frequency every 4 hours (automated)',
      'Data processing latency < 5 seconds (target: <5s)',
      'Portfolio returns beat benchmark by 2-5% (quarterly)',
      'Cost reduction 90% vs traditional tools',
      'Support for 1000+ securities with real-time processing',
      'AI-powered analysis with confidence scoring',
    ],
    lessonsLearned: [
      'The importance of real-time data processing and low-latency systems',
      'The critical role of risk management in automated trading systems',
      'The value of AI integration for market sentiment analysis',
      'The necessity of comprehensive testing for financial applications',
      'The importance of regulatory compliance in financial technology',
      'The benefits of workflow orchestration for complex financial processes',
    ],
    businessImpact: [
      'Revenue Target: $50M ARR by Year 3 through democratized access to institutional-grade tools',
      'Market Penetration: 8% of North American retail investment market',
      'Customer Acquisition: 100+ enterprise clients by Year 2',
      'ROI for Customers: < 12 months payback period',
      'Cost Efficiency: 90% reduction compared to traditional portfolio management tools',
      'Performance: Portfolio returns consistently beat benchmark by 2-5%',
      'Scalability: Support for 1000+ concurrent portfolio analyses',
      'User Adoption: Target 100+ users/month post-launch',
    ],
  },
];

export const getFeaturedProjects = (): Project[] => {
  return projectsData.filter(project => project.type);
};

export const getProjectsByCategory = (
  category: Project['category'],
): Project[] => {
  return projectsData.filter(project => project.category === category);
};

export const getProjectById = (id: string): Project | undefined => {
  return projectsData.find(project => project.id === id);
};
